import { HelpScreenshot } from '../components/dialogs/HelpScreenshot';

// CRA can be deployed under a subpath (PUBLIC_URL). If we use absolute paths like `/help-screenshots/...`,
// assets will 404 when the app is hosted at `/some/subpath`. Prefixing with PUBLIC_URL makes these work
// both locally (PUBLIC_URL="") and in production.
// In dev (`npm start`), CRA serves from `/` and `PUBLIC_URL` can be set in the shell for other tooling,
// which would break asset URLs. Only respect PUBLIC_URL in production builds.
const _publicBase =
  (process.env.NODE_ENV === 'production' ? (process.env.PUBLIC_URL || '') : '').replace(/\/$/, '');
const _asset = (path: string) => `${_publicBase}${path.startsWith('/') ? path : `/${path}`}`;

// Sample screenshot data - in a real implementation, this would come from the backend
const helpScreenshots: HelpScreenshot[] = [
  {
    id: 'load-document',
    title: 'Loading a Document from .nbr File',
    description: 'This visual guide shows you exactly how to load a document from a .nbr file created by the Narralyzer desktop application. Follow the numbered steps below.',
    imagePath: _asset('/help-screenshots/document-management/load-document.png'),
    animatedImagePath: _asset('/help-screenshots/document-management/load-document-animated.gif'),
    altText: 'Screenshot showing the Load Document feature in the document panel with file selection dialog open, including step-by-step visual guide with highlighted areas',
    steps: [
      'Click on the "Load Document" button in the document panel (highlighted in green)',
      'Select a .nbr file from your computer using the file browser',
      'The document will be imported and opened in a new tab automatically',
      'You can now work with the loaded document alongside other open documents'
    ]
  },
  {
    id: 'create-document',
    title: 'Creating a New Document',
    description: 'Learn how to create a new document from scratch and start organizing your content.',
    imagePath: _asset('/help-screenshots/document-management/create-document.png'),
    animatedImagePath: _asset('/help-screenshots/document-management/create-document-animated.gif'),
    altText: 'Screenshot showing the Create Document dialog with form fields for document name and description',
    steps: [
      'Click the "Create Document" button in the document panel',
      'Enter a name for your new document',
      'Add an optional description to help identify the document',
      'Click "Create" to start working with your new document'
    ]
  },
  {
    id: 'open-document',
    title: 'Opening a Document',
    description: 'Open a document from the document list into a tab for editing.',
    imagePath: _asset('/help-screenshots/document-management/open-document.png'),
    animatedImagePath: _asset('/help-screenshots/document-management/open-document-animated.gif'),
    altText: 'Screenshot showing how to select a document and open it from the Document Menu',
    steps: [
      'Select a document in the Document Panel (it will be highlighted)',
      'Open the "Document Menu"',
      'Click "Open" to open the selected document in a new tab',
      'The document will appear as a tab and its categories will load'
    ]
  },
  {
    id: 'close-document',
    title: 'Closing a Document',
    description: 'Close an open document tab when you are done working with it.',
    imagePath: _asset('/help-screenshots/document-management/close-document.png'),
    animatedImagePath: _asset('/help-screenshots/document-management/close-document-animated.gif'),
    altText: 'Screenshot showing how to close an open document using the Document Menu',
    steps: [
      'Select an open document (in the Open Documents list or by clicking its tab)',
      'Open the "Document Menu"',
      'Click "Close" to close the current document tab',
      'The document moves to the Closed Documents list'
    ]
  },
  {
    id: 'delete-document',
    title: 'Deleting a Document',
    description: 'Permanently delete a document you no longer need.',
    imagePath: _asset('/help-screenshots/document-management/delete-document.png'),
    animatedImagePath: _asset('/help-screenshots/document-management/delete-document-animated.gif'),
    altText: 'Screenshot showing how to delete a document using the Document Menu',
    steps: [
      'Select a document in the Document Panel',
      'Open the "Document Menu"',
      'Click "Delete"',
      'Confirm deletion in the dialog (this cannot be undone)'
    ]
  },
  {
    id: 'rename-document',
    title: 'Renaming a Document',
    description: 'Rename a document to keep your list organized and easy to search.',
    imagePath: _asset('/help-screenshots/document-management/rename-document.png'),
    animatedImagePath: _asset('/help-screenshots/document-management/rename-document-animated.gif'),
    altText: 'Screenshot showing how to rename a document using the Document Menu',
    steps: [
      'Select a document in the Document Panel',
      'Open the "Document Menu"',
      'Click "Rename"',
      'Enter the new name and confirm'
    ]
  },
  {
    id: 'duplicate-document',
    title: 'Duplicating a Document',
    description: 'Create a copy of an existing document (optionally choosing which subcases to include).',
    imagePath: _asset('/help-screenshots/document-management/duplicate-document.png'),
    animatedImagePath: _asset('/help-screenshots/document-management/duplicate-document-animated.gif'),
    altText: 'Screenshot showing how to duplicate a document using the Document Menu',
    steps: [
      'Select a document in the Document Panel',
      'Open the "Document Menu"',
      'Click "Duplicate"',
      'Choose which subcases to include',
      'Enter a name for the new document and confirm'
    ]
  },
  {
    id: 'suggest-tree-from-files',
    title: 'Suggesting a Category Tree from Files',
    description: 'Upload one or more files and let AI suggest a category tree and document name.',
    imagePath: _asset('/help-screenshots/document-management/ai-suggest-document.png'),
    animatedImagePath: _asset('/help-screenshots/document-management/ai-suggest-document-animated.gif'),
    altText: 'Screenshot showing how to suggest a category tree from files',
    steps: [
      'Open the "Document Menu"',
      'Click "Suggest category tree from files"',
      'Choose one or more files (PDF, Word, text, etc.)',
      'Optionally add context and adjust settings like maximum depth',
      'Click "Generate tree" and review the suggestion before accepting'
    ]
  },
  {
    id: 'expand-document-with-files',
    title: 'Expanding a Document with Files',
    description: 'Upload one or more files and let AI expand the selected document with new categories and content.',
    imagePath: _asset('/help-screenshots/document-management/ai-expand-document.png'),
    animatedImagePath: _asset('/help-screenshots/document-management/ai-expand-document-animated.gif'),
    altText: 'Screenshot showing how to expand a document with files',
    steps: [
      'Select a document in the Document Panel',
      'Open the "Document Menu"',
      'Click "Expand document with files"',
      'Choose one or more files (PDF, Word, text, etc.)',
      'Optionally add context and adjust settings like maximum depth',
      'Click "Generate" and review the suggestion before accepting'
    ]
  },
  {
    id: 'merge-document',
    title: 'Merging Documents',
    description: 'Combine two documents into one, choosing how to handle conflicts and missing content.',
    imagePath: _asset('/help-screenshots/document-management/merge-document.png'),
    animatedImagePath: _asset('/help-screenshots/document-management/merge-document-animated.gif'),
    altText: 'Screenshot showing how to merge documents using the Document Menu',
    steps: [
      'Open the target document (the one you want to merge into)',
      'Open the "Document Menu"',
      'Click "Merge Document"',
      'Select the source document to merge from',
      'Choose conflict/missing-content resolution options',
      'Click "Merge" to create the combined document'
    ]
  },
  {
    id: 'add-category',
    title: 'Adding Categories to Your Document',
    description: 'Organize your content by creating categories and subcategories.',
    imagePath: _asset('/help-screenshots/category-management/add-category.png'),
    altText: 'Screenshot showing the Add Category dialog with form fields for category name and description',
    steps: [
      'Select a parent category where you want to add the new category',
      'Click the "+" button next to the category name',
      'Enter a name and description for your new category',
      'Choose whether it will contain subcategories or content',
      'Click "Add" to create the category'
    ]
  },
  {
    id: 'rename-category',
    title: 'Renaming a Category',
    description: 'Change a category name to keep your tree organized and consistent.',
    imagePath: _asset('/help-screenshots/category-management/rename-category.png'),
    altText: 'Screenshot showing how to rename a category',
    steps: [
      'Select the category you want to rename',
      'Open the category actions and choose "Rename"',
      'Enter the new name and confirm'
    ]
  },
  {
    id: 'delete-category',
    title: 'Deleting a Category',
    description: 'Remove a category (and its contents) from the document.',
    imagePath: _asset('/help-screenshots/category-management/delete-category.png'),
    altText: 'Screenshot showing how to delete a category',
    steps: [
      'Select the category you want to delete',
      'Open the category actions and choose "Delete"',
      'Confirm deletion (this cannot be undone)'
    ]
  },
  {
    id: 'group-categories',
    title: 'Grouping Categories',
    description: 'Create a new parent category and move the selected categories under it.',
    imagePath: _asset('/help-screenshots/category-management/group-categories.png'),
    altText: 'Screenshot showing how to group categories',
    steps: [
      'Select two or more categories',
      'Open the category actions and choose "Group categories"',
      'Enter a name for the new parent category and confirm',
      'The selected categories become children of the new parent category'
    ]
  },
  {
    id: 'move-category',
    title: 'Moving Categories',
    description: 'Move selected categories up/down within their parent to reorganize your tree.',
    imagePath: _asset('/help-screenshots/category-management/move-category.png'),
    altText: 'Screenshot showing how to move categories',
    steps: [
      'Select one or more categories',
      'Use "Move up" / "Move down" to change their order under the same parent'
    ]
  },
  {
    id: 'copy-paste-category',
    title: 'Copying and Pasting Categories',
    description: 'Copy categories to the clipboard and paste them elsewhere to duplicate structure and content.',
    imagePath: _asset('/help-screenshots/category-management/copy-category.png'),
    altText: 'Screenshot showing how to copy categories',
    steps: [
      'Select one or more categories',
      'Choose "Copy"',
      'Select the destination category',
      'Choose "Paste" to insert a copy there'
    ]
  },
  {
    id: 'import-subtree-from-document',
    title: 'Importing a Subtree from Another Document',
    description:
      'Clone a branch from any other document under the selected category without using the clipboard.',
    imagePath: _asset('/help-screenshots/category-management/copy-category.png'),
    altText: 'Screenshot showing category toolbar with clipboard actions',
    steps: [
      'Select the destination branch category in the current document',
      'Use Import (folder upload) next to Paste, or right-click → Import subtree from document…',
      'Pick a source document, then the category subtree to clone',
      'Confirm Import'
    ]
  },
  {
    id: 'merge-categories',
    title: 'Merging Categories',
    description: 'Merge multiple selected categories into one category, preserving content and subcategories.',
    imagePath: _asset('/help-screenshots/category-management/merge-categories.png'),
    altText: 'Screenshot showing how to merge categories',
    steps: [
      'Select two or more categories',
      'Open the category actions and choose "Merge categories"',
      'Enter a name for the merged category and confirm'
    ]
  },
  {
    id: 'flatten-category',
    title: 'Flattening a Category',
    description: 'Move all subcategories up one level and remove the selected parent category.',
    imagePath: _asset('/help-screenshots/category-management/flatten-category.png'),
    altText: 'Screenshot showing how to flatten a category',
    steps: [
      'Select a category that has subcategories',
      'Open the category actions and choose "Flatten"',
      'Confirm to move the subcategories up one level and remove the parent category'
    ]
  },
  {
    id: 'split-category',
    title: 'Splitting a Category',
    description: 'Split a content category into multiple categories to better organize its contents.',
    imagePath: _asset('/help-screenshots/category-management/split-category.png'),
    altText: 'Screenshot showing the split category dialog',
    steps: [
      'Select a content category you want to split',
      'Open the category actions and choose "Split"',
      'Choose how to divide the content between the new categories',
      'Enter names for the new categories and confirm'
    ]
  },
  {
    id: 'add-content',
    title: 'Adding Content',
    description: 'Add a new content item to the selected content category.',
    imagePath: _asset('/help-screenshots/content-editor/add-content.png'),
    altText: 'Screenshot showing how to add content',
    steps: [
      'Select a content category (a category without subcategories)',
      'Click "Edit" to open the content editor',
      'Enter your text (and subcase, if relevant)',
      'Save to add the content item'
    ]
  },
  {
    id: 'filter-content-pane-subcase',
    title: 'Filtering the Content Pane by Subcase',
    description:
      'Hide content rows for subcases you do not want to see; layout matches Show sub tree selection.',
    imagePath: _asset('/help-screenshots/content-editor/add-content.png'),
    altText: 'Screenshot placeholder — content pane and toolbar area',
    steps: [
      'Open a document with multiple subcases',
      'Click the filter-list icon in the toolbar (near Find)',
      'Pick groups or individual subcases — the content table updates live',
      'Selecting all subcases clears the filter; Cancel restores the prior filter'
    ]
  },
  {
    id: 'edit-content',
    title: 'Editing Content in Categories',
    description: 'Add and edit text content within your categories.',
    imagePath: _asset('/help-screenshots/content-editor/edit-content.png'),
    altText: 'Screenshot showing the content editor with rich text formatting toolbar and editing area',
    steps: [
      'Click on a content category to open the editor',
      'Use the formatting toolbar to style your text',
      'Type or paste your content in the editing area',
      'Click "Save" to preserve your changes',
      'Your content is automatically saved as you type'
    ]
  },
  {
    id: 'search-content',
    title: 'Searching Through Your Content',
    description: 'Find specific content quickly using the search feature.',
    imagePath: _asset('/help-screenshots/search/search-content.png'),
    altText: 'Screenshot showing the search interface with search box and results highlighting',
    steps: [
      'Click the search icon in the toolbar',
      'Enter your search terms in the search box',
      'Choose between "Whole Word" or "Contains" search',
      'Toggle case sensitivity if needed',
      'Use "Find Next" and "Find Previous" to navigate results'
    ]
  },
  {
    id: 'semantic-search',
    title: 'Using Semantic Search',
    description: 'Find content using AI-powered semantic search that understands meaning.',
    imagePath: _asset('/help-screenshots/search/semantic-search.png'),
    altText: 'Screenshot showing the semantic search window with search results and similarity scores',
    steps: [
      'Open the semantic search panel from the toolbar',
      'Enter your search query in natural language',
      'Toggle semantic search on for AI-powered results',
      'Review results with similarity scores',
      'Click on results to navigate to the content'
    ]
  }
];

export class HelpScreenshotService {
    static getScreenshotsForTopic(topic: string): HelpScreenshot[] {
    // Normalize user-visible topics (sidebar strings, tooltips, free text) so simple `includes`
    // matching remains reliable even with:
    // - non‑breaking spaces
    // - multiple whitespace runs
    // - zero-width chars
    // - minor punctuation differences
    const normalize = (s: string) =>
      (s || '')
        .toLowerCase()
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const lowerTopic = normalize(topic);
    
    // Map topics to screenshot IDs
    const topicToScreenshots: { [key: string]: string[] } = {
                  // Document Management
                  'load document': ['load-document'],
                  'loading documents': ['load-document'],
                  'load file': ['load-document'],
                  'nbr file': ['load-document'],
                  'import document': ['load-document'],
                  'open file': ['load-document'],
                  'how do i load': ['load-document'],
                  'how to load': ['load-document'],
                  'loading a document': ['load-document'],
                  'load from file': ['load-document'],
                  'load from nbr': ['load-document'],
                  'import from file': ['load-document'],
                  'open nbr file': ['load-document'],
                  
                  'create document': ['create-document'],
                  'new document': ['create-document'],
                  'how to create': ['create-document'],
                  'creating documents': ['create-document'],
                  'creating a document': ['create-document'],
                  'how do i create': ['create-document'],
                  'how do i make': ['create-document'],
                  'how to make': ['create-document'],
                  'how to start': ['create-document'],
                  'making a document': ['create-document'],
                  'start new document': ['create-document'],

                  'open document': ['open-document'],
                  'opening documents': ['open-document'],
                  'opening a document': ['open-document'],
                  'open selected document': ['open-document'],
                  'document menu open': ['open-document'],
                  'how to open': ['open-document'],
                  'how do i open': ['open-document'],
                  // Variations that may come from UI/tooltips
                  'open document —': ['open-document'],
                  'open document -': ['open-document'],
                  'open document (': ['open-document'],
                  'open document select': ['open-document'],
                  
                  'close document': ['close-document'],
                  'closing documents': ['close-document'],
                  'closing a document': ['close-document'],
                  'close selected document': ['close-document'],
                  'how to close': ['close-document'],
                  'how do i close': ['close-document'],
                  'close document —': ['close-document'],
                  'close document -': ['close-document'],
                  'close document (': ['close-document'],
                  
                  'delete document': ['delete-document'],
                  'deleting documents': ['delete-document'],
                  'deleting a document': ['delete-document'],
                  'delete selected document': ['delete-document'],
                  'how to delete': ['delete-document'],
                  'how do i delete': ['delete-document'],
                  'delete document —': ['delete-document'],
                  'delete document -': ['delete-document'],
                  'delete document (': ['delete-document'],

                  'rename document': ['rename-document'],
                  'renaming documents': ['rename-document'],
                  'renaming a document': ['rename-document'],
                  'rename selected document': ['rename-document'],
                  'how to rename': ['rename-document'],
                  'how do i rename': ['rename-document'],
                  'rename document —': ['rename-document'],
                  'rename document -': ['rename-document'],
                  'rename document (': ['rename-document'],

                  'duplicate document': ['duplicate-document'],
                  'duplicating documents': ['duplicate-document'],
                  'duplicating a document': ['duplicate-document'],
                  'duplicate selected document': ['duplicate-document'],
                  'how to duplicate': ['duplicate-document'],
                  'how do i duplicate': ['duplicate-document'],
                  'duplicate document —': ['duplicate-document'],
                  'duplicate document -': ['duplicate-document'],
                  'duplicate document (': ['duplicate-document'],

                  'suggest tree from files': ['suggest-tree-from-files'],
                  'suggesting trees from files': ['suggest-tree-from-files'],
                  'suggest category tree from files': ['suggest-tree-from-files'],
                  'suggest category tree': ['suggest-tree-from-files'],
                  'suggest tree': ['suggest-tree-from-files'],
                  'ai suggest tree': ['suggest-tree-from-files'],
                  'ai suggested tree': ['suggest-tree-from-files'],
                  'ai suggest document': ['suggest-tree-from-files'],
                  'suggest document': ['suggest-tree-from-files'],
                  'upload files': ['suggest-tree-from-files'],
                  'upload file': ['suggest-tree-from-files'],
                  'import files': ['suggest-tree-from-files'],
                  'import file': ['suggest-tree-from-files'],
                  
                  'expand document with files': ['expand-document-with-files'],
                  'expanding documents with files': ['expand-document-with-files'],
                  'expand documents with files': ['expand-document-with-files'],
                  'expand document': ['expand-document-with-files'],
                  'expanding a document': ['expand-document-with-files'],
                  'expand selected document': ['expand-document-with-files'],
                  'ai expand document': ['expand-document-with-files'],
                  'ai expand': ['expand-document-with-files'],
                  'expand with files': ['expand-document-with-files'],
                  'expand from files': ['expand-document-with-files'],
                  'add files to document': ['expand-document-with-files'],
                  'append files to document': ['expand-document-with-files'],

                  'merge document': ['merge-document'],
                  'merging document': ['merge-document'],
                  'merging documents': ['merge-document'],
                  'merging a document': ['merge-document'],
                  'merge documents': ['merge-document'],
                  'merge selected document': ['merge-document'],
                  'how to merge': ['merge-document'],
                  'how do i merge': ['merge-document'],
                  'combine documents': ['merge-document'],
                  'combine document': ['merge-document'],
                  
                  // Category Management
                  'add category': ['add-category'],
                  'create category': ['add-category'],
                  'new category': ['add-category'],
                  'how to add category': ['add-category'],
                  'adding categories': ['add-category'],
                  'organize content': ['add-category'],
                  'how do i add category': ['add-category'],
                  'how to create category': ['add-category'],
                  'how do i create category': ['add-category'],
                  'making categories': ['add-category'],
                  'rename category': ['rename-category'],
                  'rename categories': ['rename-category'],
                  'renaming a category': ['rename-category'],
                  'renaming categories': ['rename-category'],
                  'how to rename category': ['rename-category'],
                  'how do i rename category': ['rename-category'],
                  'delete category': ['delete-category'],
                  'delete categories': ['delete-category'],
                  'deleting a category': ['delete-category'],
                  'deleting categories': ['delete-category'],
                  'how to delete category': ['delete-category'],
                  'how do i delete category': ['delete-category'],
                  'group categories': ['group-categories'],
                  'grouping categories': ['group-categories'],
                  'group category': ['group-categories'],
                  'group-categories': ['group-categories'],
                  'how to group categories': ['group-categories'],
                  'how do i group categories': ['group-categories'],
                  'move category': ['move-category'],
                  'moving categories': ['move-category'],
                  'move categories': ['move-category'],
                  'reorder categories': ['move-category'],
                  'copying and pasting categories': ['copy-paste-category'],
                  'copy and paste categories': ['copy-paste-category'],
                  'copy categories': ['copy-paste-category'],
                  'copy category': ['copy-paste-category'],
                  'paste categories': ['copy-paste-category'],
                  'paste category': ['copy-paste-category'],
                  'importing a subtree from another document': ['import-subtree-from-document'],
                  'import subtree from document': ['import-subtree-from-document'],
                  'import subtree': ['import-subtree-from-document'],
                  'how do i import a subtree from another document?': ['import-subtree-from-document'],
                  'merge categories': ['merge-categories'],
                  'merging categories': ['merge-categories'],
                  'merge category': ['merge-categories'],
                  'merging a category': ['merge-categories'],
                  'how to merge categories': ['merge-categories'],
                  'how do i merge categories': ['merge-categories'],
                  'flatten category': ['flatten-category'],
                  'flattening categories': ['flatten-category'],
                  'flatten categories': ['flatten-category'],
                  'flatten a category': ['flatten-category'],
                  'how to flatten': ['flatten-category'],
                  'how do i flatten': ['flatten-category'],
                  'split category': ['split-category'],
                  'splitting categories': ['split-category'],
                  'splitting a category': ['split-category'],
                  'how to split': ['split-category'],
                  'split a category': ['split-category'],
                  
                  // Content Editing
                  'edit content': ['edit-content'],
                  'content editor': ['edit-content'],
                  'writing content': ['edit-content'],
                  'add content': ['add-content'],
                  'adding content': ['add-content'],
                  'how to add content': ['add-content'],
                  'add new content': ['add-content'],
                  'text editor': ['edit-content'],
                  'rich text': ['edit-content'],
                  'how to edit content': ['edit-content'],
                  'how do i edit content': ['edit-content'],
                  'how to write content': ['edit-content'],
                  'how do i write content': ['edit-content'],
                  'how do i add content': ['add-content'],

                  'filtering the content pane by subcase': ['filter-content-pane-subcase'],
                  'how do i filter the content pane by subcase?': ['filter-content-pane-subcase'],
                  'filter content pane by subcase': ['filter-content-pane-subcase'],
                  'content pane subcase filter': ['filter-content-pane-subcase'],
                  'filter the content pane': ['filter-content-pane-subcase'],

                  // Search Features
                  'search content': ['search-content'],
                  'find content': ['search-content'],
                  'search feature': ['search-content'],
                  'how to search': ['search-content'],
                  'finding text': ['search-content'],
                  'how do i search': ['search-content'],
                  'how to find content': ['search-content'],
                  'how do i find content': ['search-content'],
                  'searching for text': ['search-content'],
                  
                  'semantic search': ['semantic-search'],
                  'ai search': ['semantic-search'],
                  'meaning search': ['semantic-search'],
                  'smart search': ['semantic-search'],
                  'how to use semantic search': ['semantic-search'],
                  'how do i use semantic search': ['semantic-search'],
                };

    // Find matching keywords in the topic, prioritizing longer matches
    const matchingScreenshotIds: string[] = [];
    
    // Sort keywords by length (longest first) to prioritize more specific matches
    const sortedKeywords = Object.entries(topicToScreenshots).sort((a, b) => b[0].length - a[0].length);
    
    for (const [keyword, screenshotIds] of sortedKeywords) {
      if (lowerTopic.includes(normalize(keyword))) {
        matchingScreenshotIds.push(...screenshotIds);
        // Break after first match to avoid cross-matching
        break;
      }
    }
    
    const screenshots = helpScreenshots.filter(screenshot => 
      matchingScreenshotIds.includes(screenshot.id)
    );
    
    return screenshots;
  }

  static getScreenshotById(id: string): HelpScreenshot | undefined {
    return helpScreenshots.find(screenshot => screenshot.id === id);
  }

  static getAllScreenshots(): HelpScreenshot[] {
    return helpScreenshots;
  }
} 